import {
    type ChatMessageLabels,
    ChatMessageProvider,
    RequestStatusBadge,
} from "@albertine/design-system/components/chat";
import type { ChatImagesOpenMessage } from "@albertine/types";
import { supportsNativeImageViewer } from "@albertine/wrapper-interop";
import { MessageScroller } from "@shadcn/react/message-scroller";
import { useAlbertineTranslation } from "albertine-shared-web";
import { parentOriginFromLocation, postMessageToParentFrame } from "@/lib/parentFrame";
import { useChatPage } from "../-hooks/useChatPage";
import { composerInsetTransition } from "../-utils/composerLayoutHeight";
import { ThreadDayGroup } from "./ThreadDayGroup";

export function ChatThreadMessages() {
    const t = useAlbertineTranslation();
    const {
        locale,
        isPending,
        isFetchingPrevious,
        dayGroups,
        imageUrls,
        requestStatusBadge,
        composerHeight,
    } = useChatPage();

    const labels: ChatMessageLabels = {
        reply: t("chat.reply"),
        copy: t("chat.copy"),
        sendingMessage: t("chat.sending_message"),
        sendingMessageFailed: t("chat.sending_message_failed"),
        preferredOptions: t("chat.preferred_options"),
        imageAttachmentAlt: t("chat.message.image_attachment_alt"),
        retrySend: t("chat.message.retry_send"),
        dismissSelection: t("chat.message.dismiss_selection"),
        addReaction: t("chat.message.add_reaction"),
        moreActions: t("chat.message.more_actions"),
        reactWith: t("chat.message.react_with"),
        removeReaction: t("chat.message.remove_reaction"),
        closeImage: t("chat.message.close_image"),
        playVoice: t("chat.message.play_voice"),
        pauseVoice: t("chat.message.pause_voice"),
        voiceProgress: t("chat.message.voice_progress"),
        downloadFile: t("chat.message.download_file"),
        openImage: t("chat.message.open_image"),
        fileAttachmentFallback: t("chat.message.file_attachment_fallback"),
        imageAttachmentFallback: t("chat.message.image_attachment_fallback"),
        replyImage: t("chat.message.reply_image"),
        replyAudio: t("chat.message.reply_audio"),
    };

    return (
        <ChatMessageProvider
            lang={locale}
            labels={labels}
            onImageLightboxChange={(open) => {
                postMessageToParentFrame(parentOriginFromLocation(), {
                    type: "chat.lightbox",
                    open,
                });
            }}
            onOpenImage={
                supportsNativeImageViewer()
                    ? (url) => openInNativeImageViewer(imageUrls, url)
                    : undefined
            }
        >
            <MessageScroller.Content
                aria-busy={isPending || isFetchingPrevious}
                className="flex min-h-full flex-col justify-end p-12"
                style={{
                    paddingBottom: composerHeight + 36,
                    transition: composerInsetTransition("padding-bottom"),
                }}
            >
                <div className="flex flex-col gap-24">
                    {dayGroups.map((group) => (
                        <ThreadDayGroup key={group.dateKey} group={group} />
                    ))}
                </div>

                {window.isIosNativeWebView ? (
                    <NativeRequestStatusBadge
                        isVisible={requestStatusBadge.isVisible}
                        title={t("chat.status_badge.title")}
                        description={t("chat.status_badge.description")}
                    />
                ) : (
                    <RequestStatusBadge
                        animate
                        isVisible={requestStatusBadge.isVisible}
                        title={t("chat.status_badge.title")}
                        description={t("chat.status_badge.description")}
                    />
                )}
            </MessageScroller.Content>
        </ChatMessageProvider>
    );
}

/** Height follows the composer inset, so the note closes as the list moves. */
function NativeRequestStatusBadge({
    description,
    isVisible,
    title,
}: {
    description: string;
    isVisible: boolean;
    title: string;
}) {
    return (
        <div
            aria-hidden={isVisible ? undefined : true}
            className="grid"
            style={{
                gridTemplateRows: isVisible ? "1fr" : "0fr",
                opacity: isVisible ? 1 : 0,
                transition: [
                    composerInsetTransition("grid-template-rows"),
                    composerInsetTransition("opacity"),
                ].join(", "),
            }}
        >
            <div className="min-h-0 overflow-hidden">
                <RequestStatusBadge isVisible title={title} description={description} />
            </div>
        </div>
    );
}

/**
 * Hands the thread's images to the native viewer, opened at the tapped one.
 * Returns `false` for an image native cannot load, such as one still uploading,
 * so the web lightbox shows it instead.
 */
function openInNativeImageViewer(urls: string[], url: string): boolean {
    const index = urls.indexOf(url);
    if (index < 0) {
        return false;
    }

    const message: ChatImagesOpenMessage = {
        type: "chat.images.open",
        images: urls.map((imageUrl) => ({ url: imageUrl })),
        index,
    };
    postMessageToParentFrame(parentOriginFromLocation(), message);
    return true;
}
